import React from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import SideBarPrivateContact from './private';
import SideBarAgentContact from './agent';
import SideBarDeveloperContact from './developer';
import { makeContactMapStateToPropsFactory, ContactPoiGetter, poiSelector } from 'components/contact/makeContactMapStateToPropsFactory';
import { ContactUnionProps, ContactType } from 'components/contact/types';
import { PoiId, PocType } from 'utils/entities';
import { Dispatch } from 'redux';
import { RootAction } from 'store/state';
import { Poi, isBulletinPoiType, isProjectPoiType } from 'utils/marketplaceRoutes';
import { contactAgentInputClick, submitContactAgent } from 'store/state/app/actions';
import { SubmitContactAgentPayload } from 'store/state/app/payloads';
import withEvents from 'analytics/withEvents';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { RequestType } from 'components/contact-modal/HebrewContactModal';
import { getContactPageEventPayload, getPOCPhoneNumber, getProjectPhoneNumber } from 'helpers/contact';
import { ContactAgentModalSource } from 'store/state/app';
import { SpacingScale } from 'ds/theme/spacing';

const poiByIdsSelector: ContactPoiGetter = (state) => poiSelector(state);

type SideBarContactEntryProps = ContactUnionProps & {
  id: PoiId;
  sidePaddingSpacing?: SpacingScale;
  source: ContactAgentModalSource;
  handleSubmitEvent?: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  onContactAgentInputClick: (eventName: string, poi: Poi) => void;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
  agentConsole?: boolean;
};


export const Root = styled.div<{ agentConsole?: boolean }>(({ theme, agentConsole }) => ({
  position: 'sticky',
  top: agentConsole ? 194 : theme.spacing(11),
  [theme.media.createObjectMediaQuery({ to: 2 })]: {
    position: 'unset',
  },
}));

export const SideBarContactEntry: React.FC<SideBarContactEntryProps> = (props) => {
  if (!props) return null;

  let content = null;

  switch (props.cardType) {
    case ContactType.Private:
      content = <SideBarPrivateContact {...props} />;
      break;
    case ContactType.Agent:
      content = <SideBarAgentContact {...props} />;
      break;
    case ContactType.Developer:
      content = <SideBarDeveloperContact {...props} />;
      break;
    default:
      content = null;
      break;
  }

  return (
    <Root data-contact-form-type="side" agentConsole={props.agentConsole}>
      {content}
      {props.children}
    </Root>
  );
};

const SideBarContactEntryWithEvents = withEvents<SideBarContactEntryProps>((sendEvent, props) => ({
  onCallClickEvent: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => {
    const contactDetails = getContactPageEventPayload(isBulletinPoiType(props.poi) && props.poi.poc, formValues, isVisible);
    const event = {
      ...contactDetails.event,
      ...(source ? { source } : null),
      ...('poc' in props.poi && props.poi.poc && props.poi.poc.type === PocType.Agent && !isVisible ? {
        agent_details: {
          id: props.poi.poc.agentId,
          office_id: props.poi.poc.officeId,
        },
      } : null),
    };

    sendEvent(isVisible ? 'property_lead_call_submit' : 'property_lead_phone_expose', 'property', {
      ...contactDetails,
      event,
      bulletinId: props.id,
      ...(isVisible ? {
        rtbData: {
          phone: isBulletinPoiType(props.poi) && getPOCPhoneNumber(props.poi.poc) || isProjectPoiType(props.poi) && getProjectPhoneNumber(props.poi),
        },
      } : null),
    });
  },
  handleSubmitEvent: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => {
    const contactDetails = getContactPageEventPayload(isBulletinPoiType(props.poi) && props.poi.poc, formValues, null, RequestType.RequestInfo);

    const event = {
      ...contactDetails.event,
      ...(source ? { source } : null),
    };

    sendEvent(hasErrors ? 'property_lead_form_submit_attempt' : 'property_lead_form_submit', 'property', {
      ...contactDetails,
      event,
      bulletinId: props.id,
    });
  },
}))(SideBarContactEntry);


const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => dispatch(submitContactAgent(payload)),
  onContactAgentInputClick: (eventName: string, poi: Poi) => dispatch(contactAgentInputClick({ eventName, poi })),
});

const makeMapStateToProps = makeContactMapStateToPropsFactory(poiByIdsSelector);
const ConnectedSideBarContactEntryProps = connect(makeMapStateToProps, mapDispatchToProps)(SideBarContactEntryWithEvents);

export default ConnectedSideBarContactEntryProps;
